
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchTrainers, saveTrainer } from "../API/API";
import "../styles/TrainerHub.css";
import RegistrationTrainer from "./RegistrationTrainer";

import defaultTrainers from "./TrainersData";

// ✅ Enhanced YouTube ID Extractor
const getYouTubeVideoId = (url) => {
  try {
    const urlObj = new URL(url);

    // Case 1: https://www.youtube.com/watch?v=VIDEO_ID
    const vParam = urlObj.searchParams.get("v");
    if (vParam) return vParam;

    // Case 2: https://youtu.be/VIDEO_ID
    if (urlObj.hostname.includes("youtu.be")) {
      return urlObj.pathname.split("/")[1];
    }

    // Case 3: https://www.youtube.com/embed/VIDEO_ID
    if (urlObj.pathname.includes("/embed/")) {
      return urlObj.pathname.split("/embed/")[1];
    }

    return null;
  } catch (err) {
    console.error("Invalid YouTube URL:", url);
    return null;
  }
};

// Utility: Convert watch or youtu.be to embed
const convertYouTubeToEmbed = (url) => {
  const videoId = getYouTubeVideoId(url);
  return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
};

function TrainerHub() {
  const [trainers, setTrainers] = useState([]);
  const [unlockedTrainers, setUnlockedTrainers] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [playingVideos, setPlayingVideos] = useState({});

  const navigate = useNavigate();

  const [newTrainer, setNewTrainer] = useState({
    name: "",
    course: "",
    contact: "",
    email: "",
    demoVideo: "",
    paidVideos: [],
  });

  useEffect(() => {
    const loadTrainers = async () => {
      try {
        const res = await fetchTrainers();
        setTrainers(res.data?.length ? res.data : defaultTrainers);
      } catch (err) {
        console.error("Error fetching trainers. Using default.", err);
        setTrainers(defaultTrainers);
      }
    };
    loadTrainers();
  }, []);

  const handleUnlock = (trainerName) => {
    setSelectedTrainer(trainerName);
  };

  const handlePaymentSuccess = () => {
    setUnlockedTrainers((prev) => ({ ...prev, [selectedTrainer]: true }));
    setSelectedTrainer(null);
    alert("You have unlocked all videos.");
  };

  const handleRegisterTrainer = async (e) => {
    e.preventDefault();
  
    const embedDemoVideo = convertYouTubeToEmbed(newTrainer.demoVideo);
    const embedPaidVideos = newTrainer.paidVideos.map((v) =>
      convertYouTubeToEmbed(v)
    );
  
    const trainerData = {
      ...newTrainer,
      demoVideo: embedDemoVideo,
      paidVideos: embedPaidVideos,
    };
  
    try {
      await saveTrainer(trainerData); // send to backend
      alert("Trainer registered successfully!");
      
      setTrainers((prev) => [...prev, trainerData]); // update UI immediately
      setNewTrainer({
        name: "",
        course: "",
        contact: "",
        email: "",
        photo: "",
        demoVideo: "",
        paidVideos: [],
      });
      setShowForm(false);
    } catch (error) {
      console.error("Error saving trainer:", error);
      alert("Failed to register trainer. Please try again.");
    }
  };

  const handleVideoClick = (trainerName, videoIndex = "demo") => {
    const key = `${trainerName}-${videoIndex}`;
    setPlayingVideos((prev) => ({ ...prev, [key]: true }));
  };

  return (
    <div className="trainer-container">
      <h1 className="trainer-hub-title">
        🎓 <span>Trainer’s Hub</span>
      </h1>
      <div className="register-btn-container">
  <button className="register-btn" onClick={() => setShowForm(true)}>
    🚀 Register as Trainer
  </button>
  
  <button
    className="register-btn"
    onClick={() => navigate("/trainerprofile")}
  >
    👥 Our Trainers
  </button>
</div>


      {showForm && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h2 className="form-heading">
              🚀 Become a <span>Freelancer Trainer</span>
            </h2>
            <form onSubmit={handleRegisterTrainer} className="trainer-form">
              <label>Name</label>
              <input
                type="text"
                value={newTrainer.name}
                onChange={(e) =>
                  setNewTrainer({ ...newTrainer, name: e.target.value })
                }
                required
              />

              <label>Contact Number</label>
              <input
                type="tel"
                value={newTrainer.contact}
                onChange={(e) =>
                  setNewTrainer({ ...newTrainer, contact: e.target.value })
                }
                required
              />

              <label>Email</label>
              <input
                type="email"
                value={newTrainer.email}
                onChange={(e) =>
                  setNewTrainer({ ...newTrainer, email: e.target.value })
                }
                required
              />

              <label>Upload Photo</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setNewTrainer({ ...newTrainer, photo: reader.result });
                  };
                  if (file) {
                    reader.readAsDataURL(file);
                  }
                }}
              />
              {newTrainer.photo && (
                <img
                  src={newTrainer.photo}
                  alt="Trainer Preview"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    marginTop: "10px",
                    borderRadius: "8px",
                  }}
                />
              )}

<label>Select Course</label>
<select
  value={newTrainer.course}
  onChange={(e) => {
    const value = e.target.value;
    setNewTrainer({
      ...newTrainer,
      course: value,
      customCourse: value !== "Other" ? "" : newTrainer.customCourse,
    });
  }}
  required
>
  <option value="">-- Choose Course --</option>
  <option value="React Native">React Native</option>
  <option value="Figma UI/UX">Figma UI/UX</option>
  <option value="Python">Python</option>
  <option value="AI & ML">AI & ML</option>
  <option value="Other">Other</option>
</select>

{newTrainer.course === "Other" && (
  <>
    <label>Custom Course Name</label>
    <input
      type="text"
      value={newTrainer.customCourse || ""}
      onChange={(e) =>
        setNewTrainer({ ...newTrainer, customCourse: e.target.value })
      }
      required
    />
  </>)}
              {/* <label>Demo Video (YouTube Link)</label>
              <input
                type="url"
                value={newTrainer.demoVideo}
                onChange={(e) =>
                  setNewTrainer({ ...newTrainer, demoVideo: e.target.value })
                }
                required
              />
              <label>Paid Videos (comma-separated)</label>
              <textarea
                value={newTrainer.paidVideos.join(", ")}
                onChange={(e) =>
                  setNewTrainer({
                    ...newTrainer,
                    paidVideos: e.target.value.split(",").map((v) => v.trim()),
                  })
                }
              /> */}
              <div className="form-buttons">
                <button type="submit" className="submit-btn">
                  Register
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {selectedTrainer && (
        <RegistrationTrainer
          onClose={() => setSelectedTrainer(null)}
          onRegistrationSuccess={handlePaymentSuccess}
        />
      )}

      <div className="trainer-list">
        {trainers.map((trainer, index) => {
          const demoKey = `${trainer.name}-demo`;
          return (
            <div className="trainer-card" key={index}>
              <div className="video-thumbnail">
                {playingVideos[demoKey] ? (
                  <iframe
                    width="100%"
                    height="200px"
                    src={trainer.demoVideo}
                    title="Demo Video"
                    frameBorder="0"
                    allowFullScreen
                  ></iframe>
                ) : (
                  <div className="demo-videos-container">
                    <div className="vidio-item">
                  <img
                    src={`https://img.youtube.com/vi/${getYouTubeVideoId(
                      trainer.demoVideo
                    )}/hqdefault.jpg`}
                    alt="Demo Thumbnail"
                    className="video-preview"
                    onClick={() => handleVideoClick(trainer.name, "demo")}
                    style={{
                      // cursor: "pointer",
                      // width: "100%",
                      // height: "auto",
                      // objectFit: "cover",
                    }}
                  />
                  </div>
                  </div>
                )}
              </div>
              <h3 className="trainer-title">{trainer.course}</h3>
              <p className="trainer-name">{trainer.name}</p>

              {!unlockedTrainers[trainer.name] ? (
                <button
                  className="unlock-btn"
                  onClick={() => handleUnlock(trainer.name)}
                >
                  Unlock More Videos
                </button>
              ) : (
                <div className="paid-videos">
                  <h4>More Videos:</h4>
                  {trainer.paidVideos.map((video, i) => {
                    const key = `${trainer.name}-${i}`;
                    return playingVideos[key] ? (
                      <iframe
                        key={key}
                        width="100%"
                        height="200px"
                        src={video}
                        title={`Paid Video ${i + 1}`}
                        frameBorder="0"
                        allowFullScreen
                      ></iframe>
                    ) : (
                      <img
                        key={key}
                        src={`https://img.youtube.com/vi/${getYouTubeVideoId(
                          video
                        )}/hqdefault.jpg`}
                        alt={`Paid Video ${i + 1}`}
                        className="video-preview"
                        onClick={() => handleVideoClick(trainer.name, i)}
                        style={{
                          cursor: "pointer",
                          width: "100%",
                          height: "200px",
                          objectFit: "cover",
                          marginBottom: "1rem",
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TrainerHub;
